import startOfTomorrow from 'date-fns/startOfTomorrow';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import {
  FlexBox,
  Typography,
  Box,
  Card,
  color,
  Button,
  SvgIconButton,
  Grid,
  SvgIcon,
  SingleActionWidget
} from '@kis-technologies/kis-ui';
import { OPERATIONAL } from 'constants/hardware';
import { selectors as configurationSelectors, Configuration } from 'services/configuration';
import { actions as eventActions, selectors as eventSelectors, EventPayload, eventNames } from 'services/events';
import { actions as hardwareActions, selectors as hardwareSelectors } from 'services/hardware';
import { selectors as kioskSelectors, actions as kioskActions, Kiosk } from 'services/kiosk';
import { selectors as maintenanceSelectors } from 'services/maintenance';
import { actions as ticketingActions } from 'services/ticketing';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { HardwareTable } from './components/HardwareTable';
import { DataEntryOverlay } from 'components/DataEntryOverlay';
import { ActionOverlay } from 'components/ActionOverlay';
import { useStyles } from './styled';

const DashboardView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const configuration = useSelector(configurationSelectors.selectConfiguration) as Configuration;
  const latestKioskEvents = useSelector(eventSelectors.selectLatestKioskEvents);
  const eventData = useSelector(hardwareSelectors.selectEventData);
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const loading = useSelector(kioskSelectors.selectLoading);
  const lastPrintedOrderDetails = useSelector(maintenanceSelectors.selectLastPrintedOrderDetails);

  const {
    getLatestEvents,
    logEvent,
    eventDataPing,
    setPrintData,
    rebootKiosk,
    reloadApplication,
    setStatusActor,
    updateKiosk,
    resetEventData
  } = useActions({
    getLatestEvents: eventActions.getLatestEvents,
    logEvent: eventActions.logEvent,
    eventDataPing: hardwareActions.eventDataPing,
    setPrintData: hardwareActions.setPrintData,
    rebootKiosk: kioskActions.rebootKiosk,
    reloadApplication: kioskActions.reloadApplication,
    setStatusActor: kioskActions.setStatusActor,
    updateKiosk: kioskActions.updateKiosk,
    resetEventData: ticketingActions.resetEventData
  });

  const enableLastOrderReprint: boolean = get(
    configuration,
    'configuration.maintenancePage.enableLastOrderReprint',
    false
  );

  const classes = useStyles();

  const [showRenameOverlay, setShowRenameOverlay] = useState(false);
  const [showRebootOverlay, setShowRebootOverlay] = useState(false);
  const [rebooting, setRebooting] = useState(false);

  React.useEffect(() => {
    getLatestEvents();
    eventDataPing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  interface KioskDetailsProps {
    label?: string;
    value?: string;
    action?: React.ReactNode;
    last?: boolean;
    testId: string;
    valueColor?: string;
  }

  const onRenameConfirm = (value: string) => {
    const payload = {
      name: value
    };
    updateKiosk(payload);
    setShowRenameOverlay(false);
  };

  const onRebootConfirm = () => {
    setRebooting(true);
    rebootKiosk();
  };

  const onStatusChange = () => {
    const payload = {
      status: kiosk.status === 'Active' ? 'Inactive' : 'Active',
      unscheduledUntil: startOfTomorrow()
    };
    const eventPayload: EventPayload = {
      event: payload.status === 'Active' ? eventNames.KIOSK_ACTIVE : eventNames.KIOSK_INACTIVE,
      category: 'active_status',
      includeActor: true
    };

    updateKiosk(payload);
    setStatusActor('maintenance');
    logEvent(eventPayload);
  };

  const onResetEventData = () => {
    const eventPayload: EventPayload = {
      event: eventNames.EVENT_DATA_REFRESHED,
      category: 'event_data_refresh',
      includeActor: true
    };

    resetEventData();
    logEvent(eventPayload);
  };

  // Set the print data and re-route to the printing page (PrintingView)
  const onReprintLastOrder = () => {
    if (lastPrintedOrderDetails) {
      setPrintData({
        printJobs: lastPrintedOrderDetails.printJobs,
        isReprint: true
      });

      navigate('/maintenance/printing');
    }
  };

  const onReloadApplication = () => {
    reloadApplication();
  };

  const getFormattedDateOrDash = (dateTime: string | undefined) =>
    dateTime ? t('{{date, timeMonthDayYear}}', { date: new Date(dateTime) }) : '--';

  const eventIcon =
    eventData.status.code === OPERATIONAL ? (
      <SvgIcon name="success_circle" size={46} color={color.GREEN} />
    ) : (
      <SvgIcon name="error_circle" color={color.RED} />
    );

  // Kiosk actions bar
  const KioskActions = () => (
    <Grid container direction="row" alignItems="flex-start" spacing={3}>
      <Grid item xl={3} md={6} lg={3}>
        <SingleActionWidget
          buttonText="Refresh Data"
          testId="event"
          leftElement={eventIcon}
          header="Event Data"
          subheader={getFormattedDateOrDash(latestKioskEvents.event_data_refresh?.createdAt)}
          buttonOnClick={onResetEventData}
        />
      </Grid>
      <Grid item xl={3} md={6} lg={3}>
        <SingleActionWidget
          buttonText="Reboot Kiosk"
          testId="reboot"
          header="Last Reboot"
          subheader={getFormattedDateOrDash(latestKioskEvents.reboot?.createdAt)}
          leftElement={<SvgIcon name="refresh" color={color.GREEN} />}
          buttonOnClick={() => setShowRebootOverlay(true)}
        />
      </Grid>
      {enableLastOrderReprint && (
        <Grid item xl={3} md={6} lg={3}>
          <SingleActionWidget
            buttonText="Reprint Last Order"
            testId="reprint"
            header="Last Order"
            subheader={getFormattedDateOrDash(lastPrintedOrderDetails?.dateTime)}
            leftElement={<SvgIcon name="ticket" size={46} color={color.GREEN} viewBox="-5 0 59 36" />}
            buttonOnClick={() => onReprintLastOrder()}
            buttonDisabled={!lastPrintedOrderDetails}
          />
        </Grid>
      )}
    </Grid>
  );
  // Kiosk details for info bax
  const KioskDetail = (kioskDetailsProps: KioskDetailsProps) => {
    const { label, value, last, testId, valueColor, action } = kioskDetailsProps;
    return (
      <Box margin="0 6" flexOverride="none" key="label">
        <FlexBox margin="6 0" height={26} align="center" direction="row">
          {label && (
            <Typography variant="body1" sx={{ width: '106px', marginRight: '40px' }} data-testid={`${testId}-label`}>
              {label}
            </Typography>
          )}
          {value && (
            <Typography variant="body1" sx={{ maxWidth: '223px' }} color={valueColor} data-testid={`${testId}-value`}>
              {value}
            </Typography>
          )}
          {action}
        </FlexBox>
        {!last && <Card.Divider data-testid={`${testId}-divider`} />}
      </Box>
    );
  };

  // Kiosk information
  const KioskInfo = () => (
    <Grid className={classes.dashboardMaintenanceKioskInfo} data-testid="kioskInfo">
      <Card raised hasBorderRadius>
        <FlexBox padding="5 6" flexOverride="none" justify="between" align="center">
          <Typography variant="h6" fontWeight={800} data-testid="kioskInfoName">
            {kiosk.name}
          </Typography>
          <SvgIconButton onClick={() => setShowRenameOverlay(true)} data-testid="rename-kiosk">
            <SvgIcon name="edit_circle" size={50} />
          </SvgIconButton>
        </FlexBox>
        <Card.Divider />
        <KioskDetail label="IP Address" value={kiosk.ipAddress} testId="kioskIPAddress" />
        <KioskDetail label="MAC Address" value={kiosk.macAddress} testId="kioskMACAddress" />
        <KioskDetail label="Location" value={(kiosk.area || { name: '' }).name} testId="kioskInfolocation" />
        <KioskDetail label="Server URL" value={process.env.REACT_APP_API_BASE} testId="kioskInfoserverIp" />
        <KioskDetail
          last
          testId="kioskReload"
          action={
            <div className={classes.dashboardReloadAppBlock}>
              <Button appearance="white" onClick={onReloadApplication} className={classes.dashboardReloadAppButton}>
                Reload Application
              </Button>
            </div>
          }
        />
      </Card>
    </Grid>
  );

  if (!kiosk.setupComplete) return <Navigate to="/systems" />;

  return (
    <Grid container className={classes.dashboardMaintenanceContainer}>
      <Grid className={classes.dashboardKioskActions} data-testid="kioskActions">
        <KioskActions />
      </Grid>
      <FlexBox fullWidth margin="4 0 0">
        <Grid container direction="row" spacing={2} className={classes.dashboardTableInfoContainer}>
          <Grid item xl={8} md={12} lg={7}>
            <HardwareTable loading={loading} statusChange={onStatusChange} />
          </Grid>
          <Grid item xl={4} md={12} lg={5}>
            <KioskInfo />
          </Grid>
        </Grid>
      </FlexBox>
      {showRenameOverlay && (
        <DataEntryOverlay
          title="Rename Kiosk"
          description="Maximum character limit is 20"
          inputHint={kiosk.name}
          inputType="keyboard"
          nextText="Rename Kiosk"
          onCancel={() => setShowRenameOverlay(false)}
          onConfirm={onRenameConfirm}
          loading={loading}
          maxLength={20}
          hideForwardPrompt
        />
      )}
      {showRebootOverlay && (
        <ActionOverlay
          title={
            rebooting ? (
              <Typography variant="h2" align="center" sx={{ lineHeight: 1 }}>
                This kiosk will <br /> reboot momentarily.
              </Typography>
            ) : (
              'Are you sure you want to reboot this kiosk?'
            )
          }
          cancelText={rebooting ? undefined : 'Reboot'}
          onCancel={onRebootConfirm}
          confirmText={rebooting ? undefined : 'Back'}
          onConfirm={() => setShowRebootOverlay(false)}
        />
      )}
    </Grid>
  );
};

export default DashboardView;
